import React from 'react'
import {
  Button,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  makeStyles,
} from '@material-ui/core'
import { Add, Delete, DragIndicator } from '@material-ui/icons'
import PropTypes from 'prop-types'
import { Formik, Form, FieldArray } from 'formik'
import * as yup from 'yup'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useMutation, useQueryClient } from 'react-query'
import DialogWithCloseIcon from '../../../../components/DialogWithCloseIcon/DialogWithCloseIcon'
import ButtonWithLoader from '../../../../components/ui/ButtonWithLoader/ButtonWithLoader'
import MyTextField from '../../../../components/form/fields/MyTextField'
import DateRangeField from '../../../../components/form/forms/AddSeasonForm/DateRangeField/DateRangeField'
import bulkCreateGamedays from '../../../../Apis/gamedays/bulkCreateGamedays'
import QueryKeys from '../../../../utils/constants/queryKeys'
import bulkAddGamedaysModalStyles from './bulkAddGamedaysModal.styles'

const useStyles = makeStyles(bulkAddGamedaysModalStyles)

const emptyRow = () => ({
  id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: '',
  startDate: null,
  endDate: null,
})

const validationSchema = yup.object().shape({
  gamedays: yup
    .array()
    .of(
      yup.object().shape({
        name: yup.string().required('Required'),
        startDate: yup.date().nullable(),
        endDate: yup
          .date()
          .nullable()
          .when('startDate', {
            is: (val) => Boolean(val),
            then: (schema) => schema.min(yup.ref('startDate'), 'End date must be after start date'),
          }),
      }),
    )
    .min(1),
})

const extractErrorMessage = (error) => {
  const response = error?.response?.data
  if (!response) return error?.message || 'Failed to create gamedays'
  if (typeof response === 'string') return response
  if (response.title) return response.title
  if (response.errors) {
    const messages = Object.values(response.errors).flat()
    if (messages.length) return messages.join(', ')
  }
  return error?.message || 'Failed to create gamedays'
}

const BulkAddGamedaysModal = (props) => {
  const { isOpen, onClose, orgId, seasonId, groupId } = props
  const classes = useStyles()
  const queryClient = useQueryClient()

  const bulkCreateMutation = useMutation((data) => bulkCreateGamedays({ orgId, data }), {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
      onClose()
    },
  })

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      groupId,
      createInAllStageGroups: values.createInAllStageGroups,
      gamedays: values.gamedays.map(({ name, startDate, endDate }) => ({
        name,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
      })),
    }

    bulkCreateMutation.mutate(payload, {
      onSettled: () => setSubmitting(false),
    })
  }

  return (
    <DialogWithCloseIcon
      open={isOpen}
      onClose={onClose}
      dialogWidth='750px'
      dialogTitle={
        <div className={classes.titleWrapper}>
          <Typography component='div' variant='h6' className={classes.title}>
            Add Gamedays
          </Typography>
        </div>
      }
    >
      <div className={classes.content}>
        <Formik
          initialValues={{ gamedays: [emptyRow()], createInAllStageGroups: false }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <div className={classes.columnHeader}>
                <div className={classes.dragCol} />
                <div className={classes.indexCol} />
                <div className={`${classes.nameCol} ${classes.columnLabel}`}>
                  Gameday Name <span className={classes.requiredMark}>*</span>
                </div>
                <div className={`${classes.dateCol} ${classes.columnLabel}`}>Date Range</div>
                <div className={classes.deleteCol} />
              </div>

              <FieldArray name='gamedays'>
                {({ push, remove, move }) => (
                  <>
                    <DragDropContext
                      onDragEnd={(result) => {
                        if (!result.destination) return
                        if (result.destination.index === result.source.index) return
                        move(result.source.index, result.destination.index)
                      }}
                    >
                      <Droppable droppableId='gamedays' direction='vertical'>
                        {(droppableProvided) => (
                          <div
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                            className={classes.rowsContainer}
                          >
                            {values.gamedays.map((row, index) => (
                              <Draggable key={row.id} draggableId={row.id} index={index}>
                                {(draggableProvided, snapshot) => (
                                  <div
                                    ref={draggableProvided.innerRef}
                                    {...draggableProvided.draggableProps}
                                    className={`${classes.row} ${snapshot.isDragging ? classes.rowDragging : ''}`}
                                    style={draggableProvided.draggableProps.style}
                                  >
                                    <div
                                      {...draggableProvided.dragHandleProps}
                                      className={`${classes.dragCol} ${classes.dragHandle}`}
                                    >
                                      <DragIndicator fontSize='small' />
                                    </div>
                                    <div className={classes.indexCol}>
                                      <div className={classes.indexBadge}>{index + 1}</div>
                                    </div>
                                    <div className={classes.nameCol}>
                                      <MyTextField
                                        name={`gamedays[${index}].name`}
                                        variant='outlined'
                                        placeholder='Gameday name'
                                      />
                                    </div>
                                    <div className={classes.dateCol}>
                                      <DateRangeField
                                        startDate={row.startDate}
                                        endDate={row.endDate}
                                        setFieldValue={(field, value) =>
                                          setFieldValue(`gamedays[${index}].${field}`, value)
                                        }
                                      />
                                    </div>
                                    <div className={classes.deleteCol}>
                                      <IconButton
                                        size='small'
                                        onClick={() => remove(index)}
                                        disabled={values.gamedays.length === 1}
                                        className={classes.deleteButton}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {droppableProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>

                    <Button
                      variant='outlined'
                      color='primary'
                      startIcon={<Add />}
                      onClick={() => push(emptyRow())}
                      className={classes.addRowButton}
                    >
                      Add another gameday
                    </Button>
                  </>
                )}
              </FieldArray>

              {bulkCreateMutation.isError && (
                <Typography variant='body2' className={classes.errorMessage}>
                  {extractErrorMessage(bulkCreateMutation.error)}
                </Typography>
              )}

              <div className={classes.footer}>
                <FormControlLabel
                  className={classes.switchControl}
                  control={
                    <Switch
                      checked={values.createInAllStageGroups}
                      onChange={(e) => setFieldValue('createInAllStageGroups', e.target.checked)}
                      color='primary'
                    />
                  }
                  label='Also create in all groups of the stage'
                />
                <div className={classes.actions}>
                  <Button onClick={onClose}>Cancel</Button>
                  <ButtonWithLoader
                    label='Create'
                    loading={isSubmitting}
                    type='submit'
                    color='primary'
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </DialogWithCloseIcon>
  )
}

BulkAddGamedaysModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  seasonId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
}

export default BulkAddGamedaysModal
